import React, { useEffect, useState } from "react";
import { StyledSummaryContainer } from "./styled";
import { MarkdownStyled } from "@/components/AiChat/comps/Messages/AI";
import { ButtonTertiaryCompact } from "oolib/dist/v2/components/Buttons";
import axios from "axios";
import { useMutation, useQuery } from "react-query";
import { SkeletonLoader, UI_CAPTION } from "oolib";

const useGenerateSummary = () => {
  return useMutation(
    ({
      activeFilters,
      dataSource,
      customPrompt,
      findQuery,
      concurrency,
      processMode,
    }) =>
      axios.post("/api/annotationExplorer/generateSummary", {
        activeFilters,
        dataSource,
        customPrompt,
        findQuery,
        concurrency,
        processMode,
      })
  );
};

const useGetSummary = ({ activeFilters, findQuery }) => {
  return useQuery({
    queryKey: ["summary", findQuery, activeFilters],
    queryFn: () =>
      axios
        .post("/api/annotationExplorer/getSummary", {
          activeFilters,
          findQuery,
        })
        .then((data) => data.data),
  });
};

export default function Summary({
  activeFilters,
  customPrompt = "",
  dataSource,
  findQuery = {},
  totalCount,
}) {
  const { data: getSummaryData, status: getSummaryStatus } = useGetSummary({
    activeFilters,
    findQuery,
  });

  useEffect(() => {
    if (getSummaryStatus === "success" && getSummaryData.summary) {
      setSummary(getSummaryData.summary);
    }
  }, [getSummaryStatus, getSummaryData]);

  const { mutate: generateSummary, status: generateSummaryStatus } =
    useGenerateSummary();

  const [summary, setSummary] = useState("");

  const handleOnClickGenerateSummary = () => {
    generateSummary(
      {
        activeFilters,
        dataSource: "annotations",
        findQuery,
        customPrompt,
        concurrency: 5,
        processMode: "concurrent",
      },
      { onSuccess: ({ data }) => setSummary(data.summary) }
    );
  };

  const canSummarize = totalCount <= 50;

  const buttonDisabled =
    generateSummaryStatus === "loading" ||
    getSummaryStatus === "loading" ||
    !canSummarize ||
    summary?.length > 0;
  const buttonText =
    generateSummaryStatus === "loading"
      ? "Generating..."
      : summary?.length > 0
      ? "Summarize Again"
      : "Summarize";

  return getSummaryStatus === "loading" ? (
    <SkeletonLoader
      key={"summary"}
      style={{ height: "4rem", borderRadius: "0.6rem" }}
    />
  ) : canSummarize && !summary ? (
    <ButtonTertiaryCompact
      style={{ background: "white" }}
      onClick={handleOnClickGenerateSummary}
      disabled={buttonDisabled}
      icon="MagicWand"
    >
      {buttonText}
    </ButtonTertiaryCompact>
  ) : (
    <StyledSummaryContainer >
      <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
        <span>
          <ButtonTertiaryCompact
            onClick={() => {}}
            disabled={buttonDisabled}
            icon="MagicWand"
          >
            {buttonText}
          </ButtonTertiaryCompact>
        </span>
        {!canSummarize ? (
          <span style={{ opacity: "50%" }}>
            <UI_CAPTION>
              {" "}
              To ensure optimal performance, only 50 annotations can be
              summarized at once. Please apply filters to narrow down your
              selection{" "}
            </UI_CAPTION>
          </span>
        ) : null}
      </div>

      {canSummarize && summary?.length > 0 ? (
        <MarkdownStyled>{summary}</MarkdownStyled>
      ) : null}
    </StyledSummaryContainer>
  );
}
